import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation  } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import {
  VideoCameraOutlined,
  ArrowLeftOutlined,
  MoneyCollectOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  BankOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleTwoTone,
  QrcodeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  Card,
  Descriptions,
  Table,
  message,
  Button,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
  Empty,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const EventShowDetail = () => {
  const { showId, dateId, timeslotId } = useParams();
  const location = useLocation();
const eventName = location.state?.eventName;
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [showInfo, setShowInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchShowBookings(pagination.current, search);
  }, [ pagination.current, search]);

const fetchShowBookings = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      // Backend event-scoped bookings endpoint
      let url = `news_and_events/event-shows/event-bookings/?event_heading=${encodeURIComponent(eventName || '')}`;
      const params = { page, page_size: pagination.pageSize };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axiosInstance.get(url, { params });

      const data = response.data;
      const results = data.results || [];

      if (results.length > 0) {
        const firstBooking = results[0];
        const totalTickets = results.reduce((sum, b) => sum + (b.number_of_tickets || 0), 0);
        const totalAmount = results.reduce((sum, b) => sum + parseFloat(b.amount_to_pay || 0), 0);
        setShowInfo({
          eventHeading: firstBooking.event?.heading || 'Unknown Event',
          totalTickets,
          totalAmount: totalAmount.toFixed(2),
          eventId: firstBooking.event?.id
        });
      }

      setBookings(results);
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        total: data.count || 0,
      });
    } catch (error) {
      console.error(error);
      message.error('Failed to load event show bookings');
      setBookings([]);
      setShowInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || 10,
    }));
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined style={{ marginRight: 6 }} />
            <strong>{record.booked_by_email || 'N/A'}</strong>
          </div>
        </div>
      ),
    },
    {
      title: 'Tickets',
      key: 'tickets_count',
      align: 'center',
      render: (_, record) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {record.number_of_tickets} ticket{record.number_of_tickets > 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Sections',
      key: 'sections',
      width: 250,
      render: (_, record) => (
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: '13px' }}>
          {record.sections?.map((section, i) => (
            <li key={i}>
              <strong>{section.title}</strong> × {section.quantity} @ {section.price}
            </li>
          )) || <em>No sections</em>}
        </ul>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'center',
      render: (_, record) => (
        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#1890ff' }}>
          {parseFloat(record.amount_to_pay || 0).toFixed(2)}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (_, record) => (
        <>
          <Tag color={record.paid ? 'green' : 'orange'}>
            {record.status || 'pending'}
          </Tag>
          <br />
          <small>{record.paid ? 'Paid' : 'Pending Payment'}</small>
        </>
      ),
    },
    {
      title: 'Verified',
      key: 'verified',
      align: 'center',
      render: (_, record) => (
        <Tag icon={<QrcodeOutlined />} color={record.is_verified ? 'green' : 'orange'}>
          {record.is_verified ? 'Verified' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Token',
      key: 'token',
      render: (_, record) => (
        <Text code style={{ fontSize: '11px', wordBreak: 'break-all', cursor: 'pointer' }} 
              onClick={() => navigator.clipboard.writeText(record.ticket_token || '')}>
          {record.ticket_token?.slice(0,8)}...
        </Text>
      ),
    },
    {
      title: 'Booked At',
      key: 'created_at',
      render: (_, record) => (
        <Text type="secondary">
          {new Date(record.created_at).toLocaleString('en-IN')}
        </Text>
      ),
    },
  ];

  if (loading && bookings.length === 0) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div className="dashboard">
      <PageHeading name="Event Show Bookings" icon={<VideoCameraOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/event/shows')}
        style={{ marginBottom: 16 }}
      >
        Back to Show List
      </Button>

{showInfo && (
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={16}>
            <Card title="Event Details" bordered={false}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Event">
                  <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{showInfo.eventHeading}</Title>
                  <Text type="secondary">(ID: {showInfo.eventId})</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Booking Summary" bordered={false}>
              <Descriptions column={1}>
                <Descriptions.Item label="Total Bookings">
                  <Title level={3} style={{ margin: 0 }}>{pagination.total}</Title>
                </Descriptions.Item>
                <Descriptions.Item label="Total Tickets">
                  <Title level={3} style={{ margin: 0 }}>{showInfo.totalTickets}</Title>
                </Descriptions.Item>
                <Descriptions.Item label="Total Amount">
                  <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{showInfo.totalAmount}</Title>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by Booking ID, Name, Email or Seat Number"
          prefix={<SearchOutlined />}
          style={{ width: '320px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Showing <strong>{bookings.length}</strong> of <strong>{pagination.total}</strong> bookings
        </Text>
      </div>

      {bookings.length === 0 ? (
        <Empty description="No bookings yet for this event show time" />
      ) : (
        <Table
          dataSource={bookings}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`,
            onChange: handlePageChange,
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No bookings found' }}
        />
      )}
    </div>
  );
};

export default EventShowDetail;

